// src/pages/admin/AdminInvestments.jsx
import React, { useEffect, useMemo, useState } from "react";

import {
  DEFAULT_INSTRUMENT_TYPE,
  instrumentMetaByValue,
  instrumentOptions,
  normalizeInstrumentType,
} from "../../constants/investments";
import api from "../../services/api";

const PAYMENT_SCHEMES = [
  { value: "bullet", label: "Bullet Payment" },
  { value: "interest_annual", label: "Annual Interest payments" },
  { value: "interest_semi_annual", label: "Semi Annual Interest" },
  { value: "interest_quarterly", label: "Quarterly Interest" },
  { value: "interest_monthly", label: "Monthly Interest" },
  { value: "amortized_monthly", label: "Monthly Principal & Interest" },
];

const BASIS_OPTIONS = [
  { value: "days", label: "Days (Actual/365)" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" },
];

const TENOR_UNITS = [
  { value: "days", label: "Days" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" },
];

const emptyTerm = {
  code: "",
  label: "",
  tenor_value: 12,
  tenor_unit: "months",
  payment_scheme: "bullet",
  calculation_basis: "",
  rate_pct: 0,
  min_amount: 0,
  allow_topups: false,
  compounding: false,
  additional_investments: false,
};

let termKeySeed = 0;
const buildTerm = (overrides = {}, index = 0, defaultBasis = "days") => {
  const merged = { ...emptyTerm, ...overrides };
  const resolvedCode = merged.code || `term_${index + 1}`;
  termKeySeed += 1;
  return {
    ...merged,
    code: resolvedCode,
    label: merged.label || `Term ${index + 1}`,
    calculation_basis: merged.calculation_basis || defaultBasis,
    __clientId: overrides.__clientId || overrides.id || `${resolvedCode}_${termKeySeed}`,
  };
};

const emptyForm = {
  slug: "",
  name: "",
  type: DEFAULT_INSTRUMENT_TYPE,
  annual_rate_pct: 0,
  min_amount: 0,
  currency: "NGN",
  description: "",
  start_date: "",
  calculation_basis: "days",
  allow_additional_investment: false,
  auto_compound: false,
  is_active: true,
  is_public: true,
  allowed_user_ids: [],
  terms: [],
};

const AdminInvestments = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [form, setForm] = useState(() => ({
    ...emptyForm,
    terms: [buildTerm({ code: "term_12m_bullet", label: "12 months Bullet" }, 0, emptyForm.calculation_basis)],
  }));
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [userToAdd, setUserToAdd] = useState("");
  const [saving, setSaving] = useState(false);
  const [simulationInput, setSimulationInput] = useState({ amount: "", termCode: "" });
  const [simulation, setSimulation] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const selectedInstrument = useMemo(
    () => instrumentMetaByValue[form.type] || instrumentMetaByValue[DEFAULT_INSTRUMENT_TYPE],
    [form.type]
  );

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.adminListOpportunities();
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      alert(error?.message || "Failed to load investment opportunities.");
    } finally {
      setLoading(false);
    }
  };

  const loadApprovedUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await api.adminListApprovedUsers();
      setApprovedUsers(Array.isArray(data) ? data : []);
      // Pre-select the first user in the dropdown for quicker additions
      if (Array.isArray(data) && data.length && !userToAdd) {
        setUserToAdd(String(data[0].id));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadApprovedUsers();
  }, []);

  const updateFormField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateTermField = (index, field, value) => {
    setForm((prev) => {
      const terms = prev.terms.map((term, idx) => (idx === index ? { ...term, [field]: value } : term));
      return { ...prev, terms };
    });
  };

  const addTerm = () => {
    setForm((prev) => {
      const nextIndex = prev.terms.length;
      const nextTerm = buildTerm(
        {
          code: `term_${nextIndex + 1}_${Date.now()}`,
          label: `New term ${nextIndex + 1}`,
          calculation_basis: prev.calculation_basis || "days",
        },
        nextIndex,
        prev.calculation_basis || "days"
      );
      return {
        ...prev,
        terms: [...prev.terms, nextTerm],
      };
    });
  };

  const removeTerm = (index) => {
    setForm((prev) => {
      if (prev.terms.length <= 1) {
        alert("An opportunity must have at least one term.");
        return prev;
      }
      const terms = prev.terms.filter((_, idx) => idx !== index);
      return { ...prev, terms };
    });
  };

  const save = async () => {
    if (!form.slug || !form.name) {
      alert("Slug and name are required.");
      return;
    }
    if (!Array.isArray(form.terms) || !form.terms.length) {
      alert("At least one term definition is required.");
      return;
    }
    const payload = {
      ...form,
      allowed_user_ids: form.is_public
        ? []
        : (form.allowed_user_ids || [])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0),
      period_options: [],
      terms: form.terms.map((term, index) => {
        const { __clientId, ...clean } = term;
        return {
          ...clean,
          display_order: index,
          payment_scheme: clean.payment_scheme || "bullet",
          calculation_basis: clean.calculation_basis || form.calculation_basis || "days",
        };
      }),
    };
    payload.type = normalizeInstrumentType(payload.type);

    setSaving(true);
    try {
      if (editingSlug && editingSlug === form.slug) {
        await api.adminUpdateOpportunity(editingSlug, payload);
      } else if (editingSlug && editingSlug !== form.slug) {
        await api.adminDeleteOpportunity(editingSlug);
        await api.adminCreateOpportunity(payload);
      } else {
        await api.adminCreateOpportunity(payload);
      }
      closeForm();
      await load();
    } catch (error) {
      console.error(error);
      alert(error?.message || "Failed to save opportunity.");
    } finally {
      setSaving(false);
    }
  };

  const handleSimulate = async () => {
    if (!editingSlug) {
      alert("Save the opportunity before running a simulation.");
      return;
    }
    const amount = Number(simulationInput.amount || 0);
    if (!amount || amount <= 0) {
      alert("Enter an amount greater than zero for the simulation.");
      return;
    }
    const termCode = simulationInput.termCode || form.terms?.[0]?.code;
    if (!termCode) {
      alert("Select a term to simulate.");
      return;
    }
    setSimLoading(true);
    try {
      const result = await api.adminSimulateOpportunity(editingSlug, {
        amount,
        term_code: termCode,
      });
      setSimulation(result);
    } catch (error) {
      console.error(error);
      alert(error?.message || "Failed to generate simulation schedule.");
    } finally {
      setSimLoading(false);
    }
  };

  const openCreate = () => {
    setForm({
      ...emptyForm,
      terms: [buildTerm({ code: "term_12m_bullet", label: "12 months Bullet" }, 0, emptyForm.calculation_basis)],
    });
    setUserToAdd(approvedUsers[0] ? String(approvedUsers[0].id) : "");
    setEditingSlug(null);
    setSimulation(null);
    setSimulationInput({ amount: "", termCode: "" });
    setShowForm(true);
  };

  const openEdit = (row) => {
    setForm({
      slug: row.id,
      name: row.name,
      type: normalizeInstrumentType(row.instrument_category || row.type),
      annual_rate_pct: row.annual_rate_pct,
      min_amount: row.min_amount,
      currency: row.currency,
      description: row.description || "",
      start_date: row.start_date || "",
      calculation_basis: row.calculation_basis || "days",
      allow_additional_investment: !!row.allow_additional_investment,
      auto_compound: !!row.auto_compound,
      is_active: !!row.is_active,
      is_public: row.is_public !== false,
      allowed_user_ids: row.allowed_user_ids || [],
      terms: (row.terms || []).map((term, index) =>
        buildTerm(
          {
            ...term,
            calculation_basis: term.calculation_basis || row.calculation_basis || "days",
            label: term.label || `Term ${index + 1}`,
            code: term.code || `term_${index + 1}`,
          },
          index,
          row.calculation_basis || "days"
        )
      ),
    });
    const firstTerm = (row.terms || [])[0] || null;
    setSimulation(null);
    setSimulationInput({
      amount: "",
      termCode: firstTerm?.code || "",
    });
    setUserToAdd(approvedUsers[0] ? String(approvedUsers[0].id) : "");
    setEditingSlug(row.id);
    setShowForm(true);
  };

  const handleDelete = async (slug) => {
    if (!window.confirm("Delete this opportunity?")) return;
    try {
      await api.adminDeleteOpportunity(slug);
      await load();
    } catch (error) {
      console.error(error);
      alert(error?.message || "Failed to delete opportunity.");
    }
  };

  const activeTerms = useMemo(() => form.terms || [], [form.terms]);

  const closeForm = () => {
    setShowForm(false);
    setEditingSlug(null);
    setSimulation(null);
    setSimulationInput({ amount: "", termCode: "" });
  };

  useEffect(() => {
    if (!activeTerms.length) {
      setSimulationInput((prev) => ({ ...prev, termCode: "" }));
      return;
    }
    if (!simulationInput.termCode || !activeTerms.find((term) => term.code === simulationInput.termCode)) {
      setSimulationInput((prev) => ({ ...prev, termCode: activeTerms[0].code }));
    }
  }, [activeTerms, simulationInput.termCode]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Fixed-income opportunities</h1>
          <p className="text-sm text-slate-500">
            Configure investment products, payment schedules, and compounding behaviours for investors.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700"
        >
          New opportunity
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Catalogue</h2>
        </div>
        {loading ? (
          <div className="px-6 py-8 text-slate-500">Loading opportunities...</div>
        ) : !rows.length ? (
          <div className="px-6 py-8 text-slate-500">No opportunities defined yet. Create one to get started.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Opportunity</th>
                  <th className="px-4 py-3 text-left font-medium">Calculation basis</th>
                  <th className="px-4 py-3 text-left font-medium">Visibility</th>
                  <th className="px-4 py-3 text-left font-medium">Additional investments</th>
                  <th className="px-4 py-3 text-left font-medium">Terms</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const rawType = row.instrument_category || row.type || "";
                  const normalizedType = normalizeInstrumentType(rawType);
                  const rawTypeLower = rawType.toLowerCase();
                  const normalizedLower = normalizedType.toLowerCase();
                  const instrument =
                    instrumentMetaByValue[rawType] ||
                    (rawType && rawTypeLower === normalizedLower ? instrumentMetaByValue[normalizedType] : null) ||
                    (!rawType ? instrumentMetaByValue[normalizedType] : null);
                  const instrumentLabel =
                    instrument?.label || rawType || instrumentMetaByValue[normalizedType]?.label;
                  return (
                    <tr key={row.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4">
                        <div className="font-semibold text-slate-900">{row.name}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-700">
                            {instrumentLabel}
                          </span>
                          <span>
                            Min {row.currency} {Number(row.min_amount || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-slate-400">
                          {row.is_active ? "Active" : "Inactive"} - Created{" "}
                          {row.created_at ? new Date(row.created_at).toLocaleDateString() : "-"}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-slate-600 capitalize">{row.calculation_basis || "days"}</td>
                      <td className="px-4 py-4 text-slate-600">
                        {row.is_public !== false
                          ? "All approved users"
                          : `Restricted (${(row.allowed_user_ids || []).length} user(s))`}
                      </td>
                      <td className="px-4 py-4 text-slate-600">
                        {row.allow_additional_investment ? "Allowed" : "Not allowed"}
                      </td>
                      <td className="px-4 py-4 text-slate-600">
                        <div className="flex flex-wrap gap-2">
                          {(row.terms || []).map((term) => (
                            <span
                              key={`${row.id}-${term.code}`}
                              className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"
                            >
                              {term.label}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            className="rounded-lg border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-100"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row.id)}
                            className="rounded-lg border border-red-200 px-3 py-1 text-xs text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/30 px-4 py-10">
          <div className="relative w-full max-w-5xl rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <header className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  {editingSlug ? "Edit opportunity" : "New opportunity"}
                </h2>
                <p className="text-xs text-slate-500">
                  Define base parameters and attach payment schedules for each term.
                </p>
              </div>
              <button
                type="button"
                onClick={closeForm}
                className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-slate-100"
              >
                ✕
              </button>
            </header>

            <div className="max-h-[75vh] overflow-y-auto px-6 py-6 space-y-6">
              <section className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-slate-600">
                    Slug <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(event) => updateFormField("slug", event.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    placeholder="unique-identifier"
                    disabled={Boolean(editingSlug)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Product name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) => updateFormField("name", event.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                    placeholder="Ecoops Fixed Income 12M"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Start date (availability)</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(event) => updateFormField("start_date", event.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                  <p className="mt-1 text-xs text-slate-500">Optional: when this offer becomes valid for investors.</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Instrument type</label>
                  <select
                    value={form.type}
                    onChange={(event) => updateFormField("type", event.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    {instrumentOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {selectedInstrument?.description ? (
                    <p className="mt-1 text-xs text-slate-500">{selectedInstrument.description}</p>
                  ) : null}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Annual base rate (%)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.annual_rate_pct}
                    onChange={(event) => updateFormField("annual_rate_pct", event.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Acts as the default coupon; individual terms can override it with their own rate.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Minimum amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.min_amount}
                    onChange={(event) => updateFormField("min_amount", event.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Currency</label>
                  <input
                    type="text"
                    value={form.currency}
                    onChange={(event) => updateFormField("currency", event.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm uppercase focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Calculation basis</label>
                  <select
                    value={form.calculation_basis}
                    onChange={(event) => updateFormField("calculation_basis", event.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm capitalize focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    {BASIS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </section>

              <section>
                <label className="text-sm font-medium text-slate-600">Description</label>
                <textarea
                  value={form.description}
                  onChange={(event) => updateFormField("description", event.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  placeholder="Investor-facing summary of the opportunity."
                />
              </section>

              <section className="rounded-xl border border-slate-200 px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Visibility & eligibility</h3>
                    <p className="text-xs text-slate-500">
                      Show this offer to everyone or limit it to specific approved users.
                    </p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-600">
                    User-facing
                  </span>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="radio"
                      name="visibility"
                      checked={form.is_public}
                      onChange={() => updateFormField("is_public", true)}
                    />
                    Visible to all approved users
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="radio"
                      name="visibility"
                      checked={!form.is_public}
                      onChange={() => updateFormField("is_public", false)}
                    />
                    Restrict to selected users
                  </label>
                </div>
                {!form.is_public && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-slate-500">
                      Pick the clients eligible for this opportunity. Their apps will show it immediately.
                    </p>
                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <select
                        value={userToAdd}
                        onChange={(event) => setUserToAdd(event.target.value)}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 md:w-2/3"
                      >
                        {usersLoading ? (
                          <option>Loading users...</option>
                        ) : !approvedUsers.length ? (
                          <option>No approved users found</option>
                        ) : (
                          approvedUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username || user.email} -{" "}
                              {user.email || user.username || "no email"}
                            </option>
                          ))
                        )}
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          if (!userToAdd) return;
                          setForm((prev) => {
                            const existing = new Set(prev.allowed_user_ids || []);
                            existing.add(Number(userToAdd));
                            return { ...prev, allowed_user_ids: Array.from(existing) };
                          });
                        }}
                        className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700 md:w-auto"
                      >
                        Add user
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(form.allowed_user_ids || []).map((id) => {
                        const user = approvedUsers.find((u) => String(u.id) === String(id));
                        const label =
                          user && `${(user.first_name || "").trim()} ${(user.last_name || "").trim()}`.trim()
                            ? `${(user.first_name || "").trim()} ${(user.last_name || "").trim()}`
                            : user?.email || user?.username || `User #${id}`;
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700"
                          >
                            {label}
                            <button
                              type="button"
                              onClick={() =>
                                setForm((prev) => ({
                                  ...prev,
                                  allowed_user_ids: (prev.allowed_user_ids || []).filter(
                                    (userId) => String(userId) !== String(id)
                                  ),
                                }))
                              }
                              className="text-emerald-700 hover:text-emerald-900"
                              aria-label={`Remove ${label}`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                      {!form.allowed_user_ids?.length && (
                        <span className="text-xs text-slate-500">No users added yet.</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Use the dropdown to add users individually. Leave empty to hide the offer until users are added.
                    </div>
                  </div>
                )}
              </section>

              <section className="flex flex-wrap gap-4 text-sm text-slate-600">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.allow_additional_investment}
                    onChange={(event) => updateFormField("allow_additional_investment", event.target.checked)}
                  />
                  Allow additional investments
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.auto_compound}
                    onChange={(event) => updateFormField("auto_compound", event.target.checked)}
                  />
                  Auto-compound interest by default
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(event) => updateFormField("is_active", event.target.checked)}
                  />
                  Active
                </label>
              </section>

              <section className="rounded-xl border border-slate-200">
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Term structures</h3>
                    <p className="text-xs text-slate-500">
                      Configure tenor, payment scheme, and compounding for each product plan.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addTerm}
                    className="rounded-lg border border-emerald-200 px-3 py-1 text-xs text-emerald-600 hover:bg-emerald-50"
                  >
                    Add term
                  </button>
                </div>
                <div className="divide-y divide-slate-200">
                  {activeTerms.map((term, index) => (
                    <div key={term.__clientId || term.code || index} className="grid gap-4 px-4 py-4 md:grid-cols-3">
                      <div className="md:col-span-3 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-slate-700">Term {index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => removeTerm(index)}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Label</label>
                        <input
                          type="text"
                          value={term.label}
                          onChange={(event) => updateTermField(index, "label", event.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Code</label>
                        <input
                          type="text"
                          value={term.code}
                          onChange={(event) => updateTermField(index, "code", event.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Tenor value</label>
                        <input
                          type="number"
                          min="1"
                          value={term.tenor_value}
                          onChange={(event) => updateTermField(index, "tenor_value", event.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Tenor unit</label>
                        <select
                          value={term.tenor_unit}
                          onChange={(event) => updateTermField(index, "tenor_unit", event.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        >
                          {TENOR_UNITS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Payment scheme</label>
                        <select
                          value={term.payment_scheme}
                          onChange={(event) => updateTermField(index, "payment_scheme", event.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        >
                          {PAYMENT_SCHEMES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Rate (%)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={term.rate_pct}
                          onChange={(event) => updateTermField(index, "rate_pct", event.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Min amount (override)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={term.min_amount}
                          onChange={(event) => updateTermField(index, "min_amount", event.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Calculation basis</label>
                        <select
                          value={term.calculation_basis || form.calculation_basis}
                          onChange={(event) => updateTermField(index, "calculation_basis", event.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm capitalize focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        >
                          {BASIS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-3 flex flex-wrap gap-4 text-xs text-slate-600">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={term.allow_topups}
                            onChange={(event) => updateTermField(index, "allow_topups", event.target.checked)}
                          />
                          Allow top-ups on this term
                        </label>
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={term.compounding}
                            onChange={(event) => updateTermField(index, "compounding", event.target.checked)}
                          />
                          Compound interest instead of periodic payouts
                        </label>
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={term.additional_investments}
                            onChange={(event) =>
                              updateTermField(index, "additional_investments", event.target.checked)
                            }
                          />
                          Allow additional investments on this term
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                <h3 className="font-semibold text-amber-900">Simulation & schedule</h3>
                <p className="text-xs">
                  Enter an amount to preview projected cashflows. Saved opportunities can generate a schedule for any term.
                </p>
                <div className="mt-3 grid gap-4 md:grid-cols-3">
                  <div>
                    <label className="text-xs font-medium text-amber-700">Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={simulationInput.amount}
                      onChange={(event) => setSimulationInput((prev) => ({ ...prev, amount: event.target.value }))}
                      className="mt-1 w-full rounded-lg border border-amber-200 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-amber-700">Term</label>
                    <select
                      value={simulationInput.termCode || activeTerms[0]?.code || ""}
                      onChange={(event) => setSimulationInput((prev) => ({ ...prev, termCode: event.target.value }))}
                      className="mt-1 w-full rounded-lg border border-amber-200 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                    >
                      {activeTerms.map((term) => (
                        <option key={term.code} value={term.code}>
                          {term.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleSimulate}
                      disabled={simLoading}
                      className="w-full rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-amber-700 disabled:opacity-60"
                    >
                      {simLoading ? "Simulating..." : "Preview schedule"}
                    </button>
                  </div>
                </div>
                {simulation && (
                  <div className="mt-4 space-y-3 rounded-lg border border-amber-200 bg-white p-3 text-xs text-slate-700">
                    <div className="flex flex-wrap gap-3 text-sm">
                      <span>
                        Total interest:{" "}
                        <strong className="text-slate-900">
                          {Number(simulation.total_interest || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </strong>
                      </span>
                      <span>
                        Principal:{" "}
                        <strong className="text-slate-900">
                          {Number(simulation.total_principal || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </strong>
                      </span>
                      <span>
                        Maturity:{" "}
                        <strong className="text-slate-900">
                          {simulation.maturity_date ? new Date(simulation.maturity_date).toLocaleDateString() : "—"}
                        </strong>
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="text-left text-slate-500">
                            <th className="px-2 py-1">#</th>
                            <th className="px-2 py-1">Due date</th>
                            <th className="px-2 py-1">Type</th>
                            <th className="px-2 py-1 text-right">Interest</th>
                            <th className="px-2 py-1 text-right">Principal</th>
                            <th className="px-2 py-1 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(simulation.cashflows || []).map((cf) => (
                            <tr key={cf.sequence}>
                              <td className="px-2 py-1">{cf.sequence}</td>
                              <td className="px-2 py-1">
                                {cf.due_date ? new Date(cf.due_date).toLocaleDateString() : "—"}
                              </td>
                              <td className="px-2 py-1 capitalize">{cf.type?.replace("_", " ")}</td>
                              <td className="px-2 py-1 text-right">
                                {Number(cf.interest || 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              <td className="px-2 py-1 text-right">
                                {Number(cf.principal || 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              <td className="px-2 py-1 text-right">
                                {Number(cf.amount || 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </section>
            </div>

            <footer className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={closeForm}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700 disabled:opacity-70"
              >
                {saving ? "Saving..." : editingSlug ? "Update opportunity" : "Create opportunity"}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvestments;
